// ATS module API layer + shared types.

export interface AtsStage {
  id: number; name: string; color: string | null; position: number;
  is_cv_sent: boolean; is_interview: boolean; is_placed: boolean;
}

export interface AtsClient {
  id: number; name: string; domain: string | null; location: string | null;
  stage: string; description: string | null; company_id: number | null;
  created_at: string; updated_at: string;
  open_roles?: number; contact_count?: number; placement_count?: number;
}

export interface AtsPerson {
  id: number; name: string; title: string | null; company: string | null;
  location: string | null; email: string | null; phone: string | null;
  linkedin_url: string | null; is_candidate: boolean; is_contact: boolean;
  candidate_id: number | null; snapshot: any;
  created_at: string; updated_at: string;
  active_cards?: number; last_activity?: string | null; client_names?: string | null;
  contact_title?: string | null; is_primary?: boolean; link_id?: number;
}

export interface AtsRole {
  id: number; client_id: number; title: string; location: string | null;
  description: string | null; employment_type: string | null;
  fee_type: string; fee_value: number | null;
  salary_min: number | null; salary_max: number | null; currency: string;
  status: string; created_at: string; updated_at: string;
  client_name?: string; active_cards?: number; rejected_cards?: number; placed_cards?: number;
}

export interface AtsCard {
  id: number; role_id: number; person_id: number; stage_id: number;
  owner_user_id: number | null; status: string; source: string | null;
  position: number; rejected_by: string | null; rejection_reason: string | null;
  rejection_note: string | null; rejected_at: string | null;
  stage_entered_at: string; created_at: string;
  // joined person fields on role detail
  name?: string; person_title?: string | null; person_company?: string | null;
  email?: string | null; phone?: string | null; linkedin_url?: string | null;
  candidate_id?: number | null; person_location?: string | null;
  // joined role fields on person detail
  role_title?: string; role_status?: string; client_name?: string;
  stage_name?: string; stage_color?: string | null;
}

export interface AtsMilestone { label: string; amount: number; booked: boolean; booked_at: string | null }

export interface AtsPlacement {
  id: number; role_id: number | null; client_id: number | null; person_id: number | null;
  client_name: string | null; person_name: string | null; role_title: string | null;
  salary: number | null; fee_amount: number | null; currency: string;
  splits: { userId: number; percent: number }[];
  start_date: string | null; end_date: string | null;
  status: string; invoice_status: string; notes: string | null; created_at: string;
  placement_type: 'contingent' | 'retained' | 'contract';
  milestones: AtsMilestone[] | null;
  charge_rate: number | null; pay_rate: number | null; rate_unit: string | null;
  units_per_week: number | null; duration_weeks: number | null;
  live_client_name?: string | null; live_person_name?: string | null; live_role_title?: string | null;
}

export interface AtsEmailAccount {
  id: number; provider: string; address: string | null; status: string;
  last_error: string | null; last_synced_at: string | null; created_at: string;
}

export interface AtsEmail {
  id: number; person_id: number | null; external_id: string; conversation_id: string;
  direction: 'inbound' | 'outbound'; from_address: string | null; to_addresses: string | null;
  subject: string | null; preview: string | null; body_text: string | null; sent_at: string;
  needs_reply: boolean; replied_at: string | null;
  counterpart_address: string | null; counterpart_name: string | null;
  classification: string;
  person_name?: string | null; person_company?: string | null;
  message_count?: number; thread_needs_reply?: boolean;
}

export interface AtsMeeting {
  id: number; external_id: string; subject: string | null;
  starts_at: string | null; ends_at: string | null; location: string | null;
  online_url: string | null; attendees: { address: string; name: string | null; personId: number | null }[];
  person_id: number | null; person_name?: string | null;
  outcome_logged_at: string | null;
}

export const KPI_LABELS: { key: string; label: string; money?: boolean }[] = [
  { key: 'cvs_sent', label: 'CVs sent' },
  { key: 'interviews', label: 'Interviews' },
  { key: 'placements', label: 'Placements' },
  { key: 'fees_booked', label: 'Fees booked', money: true },
  { key: 'candidates_added', label: 'Candidates added' },
  { key: 'activities_logged', label: 'Activities logged' },
];

export interface AtsKpis {
  period: string; start: string;
  metrics: Record<string, number>;
  targets: Record<string, number>;
}

export interface AtsTask {
  id: number; title: string; details: string | null; due_at: string | null;
  assignee_user_id: number | null; entity_type: string | null; entity_id: number | null;
  status: string; completed_at: string | null; created_at: string;
}

export interface AtsSuggestion {
  id: number; activity_id: number | null; entity_type: string; entity_id: number;
  entity_name: string | null; kind: string | null; title: string;
  due_at: string | null; confidence: number | null; source_text: string | null;
  status: string; task_id: number | null; created_at: string;
}

export interface AtsActivity {
  id: number; user_id: number; entity_type: string; entity_id: number;
  type: string; body: string | null; metadata: any; created_at: string;
}

export async function atsFetch(token: string, path: string, options?: RequestInit): Promise<any> {
  const resp = await fetch(`/api/ats${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(options?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(options?.headers || {}),
    },
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(data.error || `Request failed (${resp.status})`);
  return data;
}

export const fmtMoney = (v: number | string | null | undefined, currency = 'GBP') => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return '—';
  const sym = currency === 'GBP' ? '£' : currency === 'USD' ? '$' : currency === 'EUR' ? '€' : `${currency} `;
  return `${sym}${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

export const fmtDate = (v: string | null | undefined) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

export const timeAgo = (v: string | null | undefined) => {
  if (!v) return '';
  const ms = Date.now() - new Date(v).getTime();
  const days = Math.floor(ms / 86400000);
  if (days <= 0) {
    const hours = Math.floor(ms / 3600000);
    return hours <= 0 ? 'just now' : `${hours}h ago`;
  }
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return months < 12 ? `${months}mo ago` : `${Math.floor(months / 12)}y ago`;
};

export const CLIENT_STAGES = ['prospect', 'active', 'dormant', 'lost'] as const;
export const ROLE_STATUSES = ['lead', 'open', 'interviewing', 'filled', 'lost'] as const;

export const STATUS_BADGE: Record<string, string> = {
  prospect: 'bg-amber-50 text-amber-700 ring-amber-600/20',
  active: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
  dormant: 'bg-gray-100 text-gray-600 ring-gray-500/20',
  lost: 'bg-red-50 text-red-600 ring-red-600/20',
  lead: 'bg-amber-50 text-amber-700 ring-amber-600/20',
  open: 'bg-blue-50 text-blue-700 ring-blue-600/20',
  interviewing: 'bg-violet-50 text-violet-700 ring-violet-600/20',
  filled: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
  pending: 'bg-amber-50 text-amber-700 ring-amber-600/20',
  completed: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
  fell_through: 'bg-red-50 text-red-600 ring-red-600/20',
  not_invoiced: 'bg-gray-100 text-gray-600 ring-gray-500/20',
  invoiced: 'bg-blue-50 text-blue-700 ring-blue-600/20',
  paid: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
};
